import { mergeWith } from 'lodash-es'
import type { AllPossibleSettingsKeys, Settings } from '../../types'
import {
    SETTINGS_HOME_DISPLAY,
    SETTINGS_HOME_REGION,
    SETTINGS_HOME_SHOW,
    SETTINGS_HOME_SORT,
} from '../constants/settings'
import {
    UEOPT_HOME_DISPLAY_REGION,
    UEOPT_HOME_DISPLAY_TYPE,
    UEOPT_HOME_SHOW_BY_TYPE,
    UEOPT_HOME_SORT_BY,
    lookupUeoptById,
} from '../mfcLogic/ueopts'
import { pubSubSettings } from '../state/pubSub'
import { SETTINGSSTATE } from '../state/state.settings'
import { setCookie } from '../utilities/cookies'
import { updateSettingsAction } from './workerActions'

export const saveSettings = <
    T extends keyof Settings,
    U extends keyof Settings[T],
>(
    type: T,
    key: U,
    value: Settings[T][U],
) => {
    const setting = { type, key, value }
    const mergedValue = updateSettingsAndEmit(setting)
    const mergedSetting = {
        ...setting,
        value: mergedValue,
    }
    updateSettingsAction([mergedSetting])
}

/** Merge `setting` into `SETTINGSSTATE` */
function updateSettings<
    T extends keyof Settings,
    U extends keyof Settings[T],
>(setting: { type: T; key: U; value: Settings[T][U] }) {
    const { type, key, value } = setting
    const updatedSetting: Settings = { [key]: value }
    // create a fresh reference for that type
    const settings = { ...SETTINGSSTATE[type] }
    mergeWith(settings, updatedSetting, (target, update) => {
        if (Array.isArray(target) && Array.isArray(update)) {
            return update
        }
    })
    SETTINGSSTATE[type] = settings
    return SETTINGSSTATE[type][key]
}

export function updateSettingsAndEmit<
    T extends keyof Settings,
    U extends keyof Settings[T],
>(setting: { type: T; key: U; value: Settings[T][U] }) {
    const { key, type } = setting
    const value = updateSettings(setting)
    const newSettings = SETTINGSSTATE[type]
    // filter out any junk here - not sure if this can ever fail?
    if (newSettings) {
        pubSubSettings.emit(type, newSettings, true)
    }
    const ueoptId = lookupUeoptById(key as AllPossibleSettingsKeys)
    const cookieValue = String(value as string | number)
    setUeoptCookie(ueoptId, cookieValue)
    return value
}

function setUeoptCookie(ueoptId: number, value: string) {
    // todo: move to cookie function for settings
    switch (ueoptId) {
        case UEOPT_HOME_DISPLAY_TYPE:
            setCookie(SETTINGS_HOME_DISPLAY, value)
            return
        case UEOPT_HOME_DISPLAY_REGION:
            setCookie(SETTINGS_HOME_REGION, value.toString())
            return
        case UEOPT_HOME_SHOW_BY_TYPE:
            setCookie(SETTINGS_HOME_SHOW, value)
            return
        case UEOPT_HOME_SORT_BY:
            setCookie(SETTINGS_HOME_SORT, value)
            return
        default:
            return
    }
}
